import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import Header from './components/layout/Header';
import MiniNav from './components/layout/MiniNav';
import MobileDrawer from './components/layout/MobileDrawer';
import Ticker from './components/layout/Ticker';
import Footer from './components/layout/Footer';
import ScrollTopButton from './components/common/ScrollTopButton';
import RecentNewsDrawer from './components/common/RecentNewsDrawer';
import { useScroll } from './hooks/useScroll';
import { newsApi, statsApi, getCached, setCached } from './utils/api';import { NAV_PATH } from './constants';
import HomePage from './pages/HomePage';
import NewsPage from './pages/NewsPage';
import CategoryPage from './pages/CategoryPage';
import SamajPage from './pages/SamajPage';
import RajnitiPage from './pages/RajnitiPage';
import BicharPage from './pages/BicharPage';
import KhelkudPage from './pages/KhelkudPage';
import VividhPage from './pages/VividhPage';
import ArticlePage from './pages/ArticlePage';
import CalendarPage from './pages/CalendarPage';
import UnicodePage from './pages/UnicodePage';
import ReelsPage from './pages/ReelsPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import OurTeamPage from './pages/OurTeamPage';
import AuthorProfilePage from './pages/AuthorProfilePage';
import AdminLayout from './components/admin/AdminLayout';
import AdminLogin from './components/admin/AdminLogin';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminNews from './components/admin/AdminNews';
import AdminCategories from './components/admin/AdminCategories';
import AdminAds from './components/admin/AdminAds';
import AdminLogos from './components/admin/AdminLogos';
import AdminReels from './components/admin/AdminReels';
import AdminFooter from './components/admin/AdminFooter';
import AdminSubscribers from './components/admin/AdminSubscribers';
import AdminAbout from './components/admin/AdminAbout';
import AdminContacts from './components/admin/AdminContacts';
import AdminTeam from './components/admin/AdminTeam';
import AdminAllPhotos from './components/admin/AdminAllPhotos';
import AdminComments from './components/admin/AdminComments';
import AdminNotice from './components/admin/AdminNotice';
import AdminSettings from './components/admin/AdminSettings';
import ConnectionBanner from './components/common/ConnectionBanner';
import Favicon from './components/common/Favicon';
import NoticeModal from './components/common/NoticeModal';
import GamePage from './pages/GamePage';
import { noticeApi } from './utils/api';

const PrivateRoute = ({ children }) => {
  const token = localStorage.getItem('adminToken');
  return token ? children : <Navigate to="/admin/login" replace />;
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [newsList, setNewsList] = useState([]);
  const { scrolled, hidden } = useScroll(12);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeCat, setActiveCat] = useState('home');
  const isAdminPath = location.pathname.startsWith('/admin');
  const [noticeData, setNoticeData] = useState(null);
  const [noticeOpen, setNoticeOpen] = useState(false);

  // Site view count — harak refresh / reopening ma +1 (admin pages chhaina, SPA navigation ma count hudaina)
  useEffect(() => {
    if (!location.pathname.startsWith('/admin')) {
      statsApi.recordView().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Notice modal — session ma eak choti matra; refresh garda feri dekhaindaina
  useEffect(() => {
    if (isAdminPath) return;
    if (sessionStorage.getItem('kalonews_notice_seen') === '1') return;
    noticeApi
      .getActive()
      .then((res) => {
        if (res.data && (res.data.image || res.data.title)) {
          setNoticeData(res.data);
          setNoticeOpen(true);
          sessionStorage.setItem('kalonews_notice_seen', '1');
        }
      })
      .catch(() => {});
  }, [isAdminPath]);

  useEffect(() => {
    // CACHE FIRST — pahilo render cache bata turantai (fast loading, offline fallback)
    const cached = getCached('news_latest');
    if (cached?.data?.length) {
      setNewsList(cached.data);
    }
    fetchNews();
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const fetchNews = async () => {
    try {
      const response = await newsApi.getAll({ limit: 50 });
      const list = response.data || [];
      setNewsList(list);
      // Agami visit ko lagi cache — auto fast loading
      setCached('news_latest', list);
    } catch (error) {
      console.error('Error fetching news:', error);
      // Network fail (offline) — cached news dekhaune
      const cached = getCached('news_latest');
      if (cached?.data?.length) {
        setNewsList(cached.data);
      } else {
        setNewsList([]);
      }
    }
  };

  // Admin le news add/edit/delete garepachhi public pages turantai refresh hos
  useEffect(() => {
    const handler = () => fetchNews();
    window.addEventListener('kalonews:news-updated', handler);
    return () => window.removeEventListener('kalonews:news-updated', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openArticle = (item) => {
    if (item && item.id) {
      // Recent news drawer ko lagi localStorage ma record (max 10)
      try {
        const list = JSON.parse(localStorage.getItem('kalonews_recent') || '[]');
        const entry = { id: item.id, title: item.title, image: item.image || null, ts: Date.now() };
        const next = [entry, ...list.filter((x) => x.id !== entry.id)].slice(0, 10);
        localStorage.setItem('kalonews_recent', JSON.stringify(next));
        window.dispatchEvent(new Event('kalonews:recent-updated'));
      } catch {
        // storage fail bhaye pani article khosla
      }
      navigate(`/article/${item.id}`);
    }
  };

  const handleNavClick = (navId, query) => {
    setActiveCat(navId);
    if (navId === 'search') {
      navigate(`/news?q=${encodeURIComponent(query || '')}`);
      return;
    }
    if (navId === 'about') {
      navigate('/hamro-bare');
      return;
    }
    navigate(NAV_PATH[navId] || '/');
  };

  const slugMap = {
    politics: '/rajniti',
    sports: '/khelkud',
    entertainment: '/samaj',
    opinion: '/bichar',
    samaj: '/samaj',
    economy: '/news',
    world: '/news',
    tech: '/news',
  };

  return (
    <>
      <ScrollToTop />
      <Favicon />
      {!isAdminPath && (
        <>
          <Header
            scrolled={scrolled}
            hidden={hidden}
            onMenuOpen={() => setMenuOpen(true)}
            onGoHome={() => navigate('/')}
          />
          <MiniNav
            activeCat={activeCat}
            onNavClick={handleNavClick}
            hidden={hidden}
            onGoHome={() => navigate('/')}
            onOpenArticle={openArticle}
          />
          <Ticker news={newsList} onOpen={openArticle} />
        </>
      )}
      <MobileDrawer
        isOpen={menuOpen}
        activeCat={activeCat}
        onNavClick={handleNavClick}
        onClose={() => setMenuOpen(false)}
      />
      <ConnectionBanner />

      <Routes>
        <Route path="/" element={<HomePage onOpen={openArticle} />} />
        <Route path="/news" element={<NewsPage onOpen={openArticle} />} />
        <Route path="/category/:slug" element={<CategoryPage onOpen={openArticle} />} />
        <Route path="/samaj" element={<SamajPage onOpen={openArticle} />} />
        <Route path="/rajniti" element={<RajnitiPage onOpen={openArticle} />} />
        <Route path="/bichar" element={<BicharPage onOpen={openArticle} />} />
        <Route path="/khelkud" element={<KhelkudPage onOpen={openArticle} />} />
        <Route path="/vividh" element={<VividhPage allNews={newsList} onOpen={openArticle} />} />
        <Route path="/article/:id" element={<ArticlePage allNews={newsList} onOpen={openArticle} />} />
        <Route path="/calendar" element={<CalendarPage />} />
        <Route path="/unicode" element={<UnicodePage />} />
        <Route path="/reels" element={<ReelsPage />} />
        <Route path="/game" element={<GamePage />} />
          <Route path="/hamro-bare" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/hamro-tim" element={<OurTeamPage />} />
        <Route path="/profile/:author" element={<AuthorProfilePage onOpen={openArticle} />} />
        <Route path="/about" element={<AboutPage />} />

        <Route
          path="/admin/login"
          element={<AdminLogin onSuccess={() => navigate('/admin/dashboard')} onClose={() => navigate('/')} />}
        />
        <Route path="/admin" element={<PrivateRoute><AdminLayout /></PrivateRoute>}>
          <Route index element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="news" element={<AdminNews />} />
          <Route path="categories" element={<AdminCategories />} />
          <Route path="subscribers" element={<AdminSubscribers />} />
          <Route path="reels" element={<AdminReels />} />
          <Route path="ads" element={<AdminAds />} />
          <Route path="logos" element={<AdminLogos />} />
          <Route path="footer" element={<AdminFooter />} />
          <Route path="ourteam" element={<AdminTeam />} />
          <Route path="allphotos" element={<AdminAllPhotos />} />
          <Route path="comments" element={<AdminComments />} />
          <Route path="about" element={<AdminAbout />} />
          <Route path="notice" element={<AdminNotice />} />
          <Route path="contacts" element={<AdminContacts />} />
          <Route path="setting" element={<AdminSettings />} />
        </Route>
      </Routes>

      {!isAdminPath && (
        <Footer
          onCategoryClick={(catId) => navigate(slugMap[catId] || '/news')}
        />
      )}

      {/* Desktop-only floating recent news drawer */}
      {!isAdminPath && <RecentNewsDrawer />}

      {/* Notice modal — photo + title clickable with redirect link */}
      {!isAdminPath && (
        <NoticeModal
          open={noticeOpen}
          notice={noticeData}
          onClose={() => setNoticeOpen(false)}
          onOpenLink={(link) => {
            setNoticeOpen(false);
            if (/^https?:\/\//i.test(link)) window.open(link, '_blank', 'noopener,noreferrer');
            else navigate(link);
          }}
        />
      )}

      <ScrollTopButton />
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <div className="kalo-root">
          <AppContent />
          <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
        </div>
      </Router>
    </HelmetProvider>
  );
}

export default App;